import { createContext, useContext, useState } from 'react';
import type { Resource } from '../../lib/resources';

export type DashboardView = 'business' | 'resource';

const STORAGE_PREFIX = 'nexus-dashboard-view:';

/** A team member with admin permissions. The placeholder admin (no team record) is not one. */
export function resourceAdminId(resource: Resource | null | undefined): string | null {
  if (!resource?.is_admin || !resource.auth_user_id) return null;
  if (resource.id === resource.auth_user_id) return null;
  return resource.id;
}

export function readDashboardView(resourceId: string): DashboardView {
  try {
    return localStorage.getItem(STORAGE_PREFIX + resourceId) === 'resource' ? 'resource' : 'business';
  } catch {
    return 'business';
  }
}

function writeDashboardView(resourceId: string, view: DashboardView) {
  try {
    localStorage.setItem(STORAGE_PREFIX + resourceId, view);
  } catch {
    // The choice still applies for this visit if storage is blocked.
  }
}

interface DashboardViewValue {
  canToggle: boolean;
  view: DashboardView;
  choose: (view: DashboardView) => void;
}

const DashboardViewContext = createContext<DashboardViewValue>({
  canToggle: false,
  view: 'business',
  choose: () => {},
});

export function DashboardViewProvider({
  resourceId,
  children,
}: {
  resourceId: string | null;
  children: React.ReactNode;
}) {
  const [choice, setChoice] = useState<{ id: string; view: DashboardView } | null>(null);
  const view = choice && choice.id === resourceId
    ? choice.view
    : resourceId
      ? readDashboardView(resourceId)
      : 'business';

  const choose = (next: DashboardView) => {
    if (!resourceId) return;
    writeDashboardView(resourceId, next);
    setChoice({ id: resourceId, view: next });
  };

  return (
    <DashboardViewContext.Provider value={{ canToggle: !!resourceId, view, choose }}>
      {children}
    </DashboardViewContext.Provider>
  );
}

export function useDashboardView() {
  return useContext(DashboardViewContext);
}

/** Business home or this person's own dashboard. Hidden unless they are a resource admin. */
export function DashboardViewToggle() {
  const { canToggle, view, choose } = useDashboardView();
  if (!canToggle) return null;

  const options: { id: DashboardView; label: string }[] = [
    { id: 'business', label: 'Business' },
    { id: 'resource', label: 'My dashboard' },
  ];

  return (
    <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5" role="group" aria-label="Dashboard">
      {options.map((option) => {
        const selected = view === option.id;
        return (
          <button
            key={option.id}
            type="button"
            aria-pressed={selected}
            onClick={() => choose(option.id)}
            className={`rounded-md px-2.5 py-1 text-[12px] font-semibold transition-colors ${
              selected
                ? 'border border-gray-200 bg-white text-gray-900 shadow-sm'
                : 'border border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
